import * as Sentry from "@sentry/nuxt"
import { EVENTS, track, numeric, enumValue, entityId, currentPageRoute } from "~/utils/analytics"

const generateFilterQuery = (filters, date = undefined) => {
	let filterQuery = ""

	if (filters.length) {
		filterQuery =
			filterQuery +
			"&filter=" +
			encodeURIComponent(
				JSON.stringify(
					filters.map(([key, value, exact, not]) => {
						return { key: key, value: value, exact, not }
					})
				)
			)
	}

	if (date) {
		filterQuery = filterQuery + "&date=" + JSON.stringify(date)
	}
	return filterQuery
}

const settingsModule = {
	namespaced: true,
	state: () => ({
		settings: [],
		settingLoading: false,
		chosenSetting: false,
	}),
	mutations: {
		SET_SETTINGS(state, payload) {
			state.settings = payload
		},
		SET_SETTING_LOADING(state, payload) {
			state.settingLoading = payload
		},
		SET_CHOSEN_SETTING(state, payload) {
			state.chosenSetting = payload
		},
	},
	actions: {
		async getSettings({ commit, dispatch }, payload) {
			const filters = payload?.filters || []
			const filterQuery = generateFilterQuery(filters)
			const page = payload?.page || 1
			const limit = payload?.limit || 20
			const query = `?limit=${limit}&page=${page}${filterQuery}`
			const config = {
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
					"content-type": "application/json",
				},
			}
			try {
				const response = await $fetch<{
					settings: any[]
					paginate: {
						limit: number
						offset: number
						order: string[][]
						total: number
					}
				}>(`${import.meta.env.VITE_AND_DINE_API}/api/users/settings/allSettings${query}`, config)

				commit("SET_SETTINGS", response.settings)
				commit("SET_PAGES", response.paginate, { root: true })
			} catch (error: any) {
				Sentry.captureException(error)
				dispatch("handleError", { error }, { root: true })
			}
		},
		async getAllSettings({ commit }) {
			const config = {
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
					"content-type": "application/json",
				},
			}
			try {
				const response = await $fetch<{
					settings: any[]
					paginate: {
						limit: number
						offset: number
						order: string[][]
						total: number
					}
				}>(`${import.meta.env.VITE_AND_DINE_API}/api/users/settings/allSettings`, config)

				commit("SET_SETTINGS", response.settings)
			} catch (error: any) {
				Sentry.captureException(error)
			}
		},
		async createSetting({ commit, dispatch }, payload) {
			const config = {
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
					"content-type": "application/json",
				},
			}

			try {
				commit("SET_SETTING_LOADING", true)

				const response = await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/settings`, {
					method: "POST",
					body: payload,
					...config,
				})

				commit("SET_SETTING_LOADING", false)

				track(EVENTS.SETTING_SAVED, {
					operation: "created",
					setting_id: entityId((response as any)?.id),
					setting_key: enumValue(payload?.key),
					is_silent: false,
					page_route: currentPageRoute(),
				})

				commit("SET_SUCCESS_MESSAGE", "Successfully created setting", { root: true })
				setTimeout(() => {
					commit("SET_SUCCESS_MESSAGE", false, { root: true })
					navigateTo(`/settings/${response.id}`)
				}, 5000)
			} catch (error) {
				commit("SET_SETTING_LOADING", false)
				dispatch("handleError", { error, description: "create setting" }, { root: true })
				Sentry.captureException(error)
			}
		},
		// Create a setting without the navigation side-effect of `createSetting`.
		// Used to self-seed fixed settings (e.g. homepage_banners) from a page.
		// Returns the created setting; re-throws so callers can handle 409 (already exists).
		async createSettingSilent(_ctx, payload) {
			const config = {
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
					"content-type": "application/json",
				},
			}
			const response = await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/settings`, {
				method: "POST",
				body: payload,
				...config,
			})
			track(EVENTS.SETTING_SAVED, {
				operation: "created",
				setting_id: entityId((response as any)?.id),
				setting_key: enumValue(payload?.key),
				is_silent: true,
				page_route: currentPageRoute(),
			})
			return response
		},
		async updateSetting({ commit, dispatch }, payload) {
			const config = {
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
					"content-type": "application/json",
				},
			}

			try {
				commit("SET_SETTING_LOADING", true)

				// Strip immutable key — backend rejects PUT bodies that include it.
				const { key: _key, ...putBody } = payload

				await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/settings/${payload.id}`, {
					method: "PUT",
					body: putBody,
					...config,
				})

				const response = await $fetch(
					`${import.meta.env.VITE_AND_DINE_API}/api/users/settings/${payload.id}`,
					config
				)

				commit("SET_CHOSEN_SETTING", response)
				commit("SET_SETTING_LOADING", false)
				track(EVENTS.SETTING_SAVED, {
					operation: "updated",
					setting_id: entityId(payload?.id),
					setting_key: enumValue(payload?.key),
					is_silent: false,
					page_route: currentPageRoute(),
				})
				commit("SET_SUCCESS_MESSAGE", "Successfully updated setting", { root: true })
				setTimeout(() => {
					commit("SET_SUCCESS_MESSAGE", false, { root: true })
				}, 5000)
			} catch (error) {
				commit("SET_SETTING_LOADING", false)
				dispatch("handleError", { error, description: "updated setting" }, { root: true })
				Sentry.captureException(error)
			}
		},
		async getSettingById({ commit, dispatch }, payload) {
			const config = {
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
					"content-type": "application/json",
				},
			}
			try {
				const response = await $fetch<any[]>(
					`${import.meta.env.VITE_AND_DINE_API}/api/users/settings/${payload}`,
					config
				)

				commit("SET_CHOSEN_SETTING", response)
			} catch (error: any) {
				Sentry.captureException(error)
				dispatch("handleError", { error }, { root: true })
			}
		},
	},
}

export default settingsModule
